import { supabase } from '../../config/supabase';
import { UserCategoryPreference } from '../../types/category';
import logger from '../../utils/logger';

/**
 * Convertit les données Supabase vers le format de l'application
 */
function toAppFormat(data: any): UserCategoryPreference {
  return {
    id: data.id,
    userId: data.user_id,
    categoryId: data.category_id,
    displayOrder: data.display_order,
    createdAt: data.created_at ? new Date(data.created_at) : undefined,
    updatedAt: data.updated_at ? new Date(data.updated_at) : undefined
  };
}

/**
 * Service pour gérer les préférences utilisateur des catégories
 */
export const userCategoryPreferencesService = {
  /**
   * Récupère les préférences de l'utilisateur connecté
   */
  async getUserPreferences(): Promise<UserCategoryPreference[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        logger.warn('Aucun utilisateur connecté');
        return [];
      }

      const { data, error } = await supabase
        .from('user_category_preferences')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true });

      if (error) {
        logger.error('Erreur lors de la récupération des préférences:', error);
        throw error;
      }

      return (data || []).map(toAppFormat);
    } catch (error) {
      logger.error('Erreur lors de la récupération des préférences:', error);
      throw error;
    }
  },

  /**
   * Crée ou met à jour une préférence utilisateur
   */
  async upsertPreference(categoryId: string, displayOrder: number): Promise<UserCategoryPreference> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const { data, error } = await supabase
        .from('user_category_preferences')
        .upsert({
          user_id: user.id,
          category_id: categoryId,
          display_order: displayOrder
        }, {
          onConflict: 'user_id,category_id'
        })
        .select()
        .single();

      if (error) {
        logger.error(`Erreur lors de la mise à jour de la préférence pour ${categoryId}:`, error);
        throw error;
      }

      return toAppFormat(data);
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de la préférence:', error);
      throw error;
    }
  },

  /**
   * Met à jour l'ordre d'affichage de plusieurs catégories
   */
  async updateDisplayOrders(updates: Array<{ categoryId: string; displayOrder: number }>): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      logger.info(`Mise à jour de l'ordre d'affichage de ${updates.length} catégories pour l'utilisateur ${user.id}`);

      // Utiliser upsert pour créer ou mettre à jour chaque préférence
      for (const update of updates) {
        await this.upsertPreference(update.categoryId, update.displayOrder);
      }

      logger.info('Ordre d\'affichage des catégories mis à jour avec succès');
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'ordre d\'affichage:', error);
      throw error;
    }
  },

  /**
   * Initialise les préférences pour une nouvelle catégorie
   */
  async initializePreferenceForCategory(categoryId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Récupérer le nombre actuel de préférences pour définir le displayOrder
      const preferences = await this.getUserPreferences();
      const maxOrder = preferences.length > 0 
        ? Math.max(...preferences.map(p => p.displayOrder))
        : 0;

      await this.upsertPreference(categoryId, maxOrder + 1);
      
      logger.info(`Préférence initialisée pour la catégorie ${categoryId}`);
    } catch (error) {
      logger.error('Erreur lors de l\'initialisation de la préférence:', error);
      throw error;
    }
  },

  /**
   * Supprime une préférence utilisateur
   */
  async deletePreference(categoryId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      const { error } = await supabase
        .from('user_category_preferences')
        .delete()
        .eq('user_id', user.id)
        .eq('category_id', categoryId);

      if (error) {
        logger.error(`Erreur lors de la suppression de la préférence pour ${categoryId}:`, error);
        throw error;
      }

      logger.info(`Préférence supprimée pour la catégorie ${categoryId}`);
    } catch (error) {
      logger.error('Erreur lors de la suppression de la préférence:', error);
      throw error;
    }
  }
};
